// Pick one employee in a modal — the Add Loan picker, shared by Add/Edit Loan,
// Add/Edit Manual Adjustment, Add Time Record and Add/Edit Adjustment
// (PRD_SharedEmployeePicker C1, C2).
//
// Click to browse or type to search (the #340 rule, department included — none
// of these screens has a department dropdown). Rows render and sort from any
// shape the screens hold via the employeeSearch accessors, and onChange hands
// back the caller's OWN row object, so each screen keeps its select handler.
//
// Keyboard (C4): ↓ / ↑ move a highlight, Enter picks; the rows and the list box
// sit out of the Tab order (Chrome makes a scrolling box a Tab stop), so Tab
// moves on to the next field. Esc is the shared <Modal>'s — it
// closes the whole window (PRD_ModalBehavior C2); nothing here handles it.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { employeeDepartment, employeeFullName, matchesEmployeeSearch } from "../utils/employeeSearch";

// PRD_LoansManagement C14(c) — pinned copy; LoansPage.js re-exports it.
export const INACTIVE_BADGE = "Employee inactive";

export const inactiveBadge = (
  <span style={{ background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)", padding: "0.125rem 0.5rem", borderRadius: "20px", fontSize: "0.6875rem", fontWeight: 600, whiteSpace: "nowrap" }}>
    {INACTIVE_BADGE}
  </span>
);

const inputStyle = {
  display: "block",
  width: "100%",
  padding: "0.5rem 0.75rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  fontSize: "0.8125rem",
  fontFamily: "inherit",
};
const changeBtn = {
  padding: "0.3rem 0.55rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  background: "white",
  cursor: "pointer",
  fontSize: "0.75rem",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
};
const selectedCard = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem",
  border: "1px solid var(--ui-border)", borderRadius: "6px", padding: "0.5rem 0.625rem", marginBottom: "0.75rem",
};
const pickerAvatar = {
  width: "28px", height: "28px", borderRadius: "50%", background: "var(--ui-primary-tint)", color: "var(--ui-primary)",
  fontSize: "11px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
};
const dropdownBox = {
  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, marginTop: "2px",
  background: "white", border: "1px solid var(--ui-border)", borderRadius: "6px",
  maxHeight: "200px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
};
// flexWrap: a row too wide for its list wraps instead of scrolling the list
// sideways. The ID and name are one group (idNameGroup), so on a phone the name
// wraps beside the ID and the badge / department move to the next line.
const dropdownItem = {
  display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0.75rem",
  fontSize: "0.8125rem", cursor: "pointer", borderBottom: "1px solid var(--ui-surface-recessed)",
};
const idNameGroup = { display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 };
// C2 — the ID never wraps, so a long name can't split EMP-#### across lines on a phone.
const idStyle = { color: "var(--ui-text-muted)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };

const idOf = (e) => e?.employeeId || e?.employeeCode || "";

const initials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

// C6 — strict: only Loans' rows carry activeToday, so no other screen ever badges.
const badgeFor = (e) => (e?.activeToday === false ? inactiveBadge : null);

export default function EmployeePicker({ employees, value, onChange, locked, emptyText, disabled }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const changeRef = useRef(null);
  const listRef = useRef(null);
  // C4 — where focus goes once the next render has mounted it: the card's
  // Change button after a pick, the input after Change.
  const pendingFocus = useRef(null);

  const matches = useMemo(
    () =>
      (employees || [])
        .filter((e) => matchesEmployeeSearch(e, search, [employeeDepartment(e)]))
        // C2 — through the accessor: payroll-run rows and roster docs have no top-level fullName.
        .sort((a, b) => employeeFullName(a).localeCompare(employeeFullName(b))),
    [employees, search]
  );

  useEffect(() => {
    const target = pendingFocus.current === "change" ? changeRef.current : pendingFocus.current === "input" ? inputRef.current : null;
    if (target) {
      pendingFocus.current = null;
      target.focus();
    }
  });

  useEffect(() => {
    // Optional call: jsdom (16.7.0) has no scrollIntoView.
    if (open) listRef.current?.children[highlight]?.scrollIntoView?.({ block: "nearest" });
  }, [open, highlight]);

  const pick = (row) => {
    pendingFocus.current = "change";
    setSearch("");
    setOpen(false);
    onChange(row);
  };

  const onKeyDown = (ev) => {
    // C4 — every handled key is default-prevented: an Enter that is not would
    // land its follow-on keypress on the Change button focus has just moved to.
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(matches.length - 1, 0)));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      if (open && matches[highlight]) pick(matches[highlight]);
    }
  };

  const name = employeeFullName(value);

  if (locked) {
    return (
      <div style={{ marginBottom: "0.75rem", fontSize: "0.8125rem" }}>
        {name || "Employee"}
        {idOf(value) && <>{" "}<span style={idStyle}>EMP-{idOf(value)}</span></>}
        {badgeFor(value) && <> {badgeFor(value)}</>}
      </div>
    );
  }

  if (value) {
    return (
      <div style={selectedCard}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", minWidth: 0 }}>
          <div style={pickerAvatar}>{initials(name)}</div>
          <span style={{ fontSize: "0.8125rem" }}>
            <span style={idStyle}>EMP-{idOf(value)}</span>
            {" - "}{name}
            {badgeFor(value) && <> {badgeFor(value)}</>}
          </span>
        </div>
        <button
          type="button"
          ref={changeRef}
          onClick={() => { pendingFocus.current = "input"; setSearch(""); onChange(null); }}
          style={changeBtn}
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div
      style={{ position: "relative", marginBottom: "0.75rem" }}
      // C4 — close only when focus leaves the picker; a clicked row (tabIndex -1)
      // takes focus inside it, so the click still lands.
      onBlur={(ev) => { if (!ev.currentTarget.contains(ev.relatedTarget)) setOpen(false); }}
    >
      <input
        ref={inputRef}
        type="text"
        placeholder="Click to browse, or type to search…"
        value={search}
        disabled={disabled}
        onChange={(ev) => { setSearch(ev.target.value); setHighlight(0); setOpen(true); }}
        onFocus={() => { setOpen(true); setHighlight(0); }}
        onKeyDown={onKeyDown}
        style={inputStyle}
      />
      {open && (
        <div ref={listRef} tabIndex={-1} data-testid="employee-picker-list" style={dropdownBox}>
          {matches.length === 0 ? (
            <div style={{ padding: "0.75rem", color: "var(--ui-text-muted)", fontSize: "0.8125rem" }}>{emptyText || "No employees found."}</div>
          ) : (
            matches.map((e, i) => (
              <div
                key={e.id || idOf(e)}
                role="button"
                tabIndex={-1}
                onClick={() => pick(e)}
                style={i === highlight ? { ...dropdownItem, background: "var(--ui-primary-tint)" } : dropdownItem}
              >
                <span style={idNameGroup}>
                  <span style={idStyle}>EMP-{idOf(e)}</span>
                  <span>{employeeFullName(e)}</span>
                </span>
                {badgeFor(e)}
                {employeeDepartment(e) && <span style={{ marginLeft: "auto", color: "var(--ui-text-muted)", fontSize: "0.75rem" }}>{employeeDepartment(e)}</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
